/**
 * EvidenciaCaptura — Pantalla intermedia antes del check-in en muelle.
 *
 * Flujo simplificado (migración 086):
 *   1. Operador escoge qué fotografía:
 *        - 📷 Foto del documento (tipo_foto='documento')
 *        - 👤 Foto de la persona  (tipo_foto='persona')   — si no porta documento
 *   2. Sube la imagen comprimida (canvas → JPEG 1600px / 85%)
 *   3. Backend guarda la foto. NO ejecuta OCR.
 *   4. Operador confirma el check-in.
 *
 * El camino de omisión sin foto ya no existe: la foto es obligatoria en ambas
 * modalidades. Si el titular no porta documento, se fotografía a la persona.
 *
 * Captura: <input capture="environment"> abre la cámara nativa del móvil sin
 * pasar por galería. Compresión cliente-side para minimizar el upload.
 */
import React, { useState, useRef } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, Avatar, Divider,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function authHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
function getUserId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || null; } catch { return null; }
}
function getUserName() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').name || ''; } catch { return ''; }
}

// Comprime una imagen del input file a JPEG ≤1600px / 85% quality
async function comprimirImagen(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const ratio = Math.min(maxDim / img.width, maxDim / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('canvas vacío')), 'image/jpeg', quality);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function EvidenciaCaptura({ reserva, onConfirm, onCancel, onError }) {
  const [tipoFoto, setTipoFoto] = useState(null);       // 'documento' | 'persona'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const inputRef = useRef(null);

  // Abrir cámara para el tipo escogido. Resetea cualquier captura previa.
  const elegirTipo = (tipo) => {
    setTipoFoto(tipo);
    setPreviewUrl(null);
    setGuardado(false);
    // Pequeño defer para que React monte el input antes de hacer click.
    setTimeout(() => inputRef.current?.click(), 50);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // permitir re-seleccionar el mismo archivo
    if (!file || !tipoFoto) return;
    setSubiendo(true);
    try {
      const blob = await comprimirImagen(file);
      setPreviewUrl(URL.createObjectURL(blob));

      // Upload del archivo
      const fd = new FormData();
      fd.append('archivo', blob, `evidencia-${reserva.codigo_reserva}.jpg`);
      const t = localStorage.getItem('token');
      const upR = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd,
      });
      if (!upR.ok) throw new Error('Error subiendo la foto');
      const upD = await upR.json();

      // Registrar evidencia en el muelle (sin OCR).
      const evR = await fetch(`${API_BASE}/turismo/muelle/reservas/${reserva.id}/evidencia`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          foto_url: upD.url,
          tipo_foto: tipoFoto,
          capturada_por_user_id: getUserId(),
          capturada_por_nombre: getUserName(),
        }),
      });
      if (!evR.ok) {
        const err = await evR.json().catch(() => ({}));
        throw new Error(err.detail || 'Error registrando evidencia');
      }
      setGuardado(true);
    } catch (err) {
      onError?.(err.message);
    }
    setSubiendo(false);
  };

  const tipoLabel = tipoFoto === 'documento' ? 'Foto del documento' : 'Foto de la persona';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Avatar sx={{ bgcolor: '#fef3c7', color: '#92400e', width: 40, height: 40 }}>
          <CameraAltIcon sx={{ fontSize: 22 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
            Evidencia de check-in
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            {reserva.nombre_cliente}
          </Typography>
        </Box>
      </Box>

      {/* Paso 1 — escoger qué foto: documento o persona */}
      {!tipoFoto && (
        <>
          <Alert severity="info" sx={{ fontSize: 11.5, mb: 2 }}>
            Toma <strong>una</strong> de las dos fotos. Si el titular trae documento,
            fotografía el documento. Si no lo trae, fotografía a la persona.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="contained" size="large" disableElevation
              startIcon={<BadgeIcon />} onClick={() => elegirTipo('documento')}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.5,
                     background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              📷 Foto del documento
            </Button>
            <Button variant="outlined" size="large"
              startIcon={<PersonIcon />} onClick={() => elegirTipo('persona')}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.5,
                     borderColor: '#0d9488', color: '#0d9488' }}>
              👤 Foto de la persona (sin documento)
            </Button>
          </Box>
        </>
      )}

      {/* Input oculto: se abre cuando se escoge el tipo */}
      <input ref={inputRef} hidden type="file" accept="image/*"
        capture="environment" onChange={handleFile} />

      {/* Paso 2 — preview + estado de subida */}
      {tipoFoto && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
              {tipoLabel}
            </Typography>
            {!subiendo && !guardado && (
              <Button size="small" onClick={() => { setTipoFoto(null); setPreviewUrl(null); }}
                sx={{ textTransform: 'none', fontSize: 11, color: '#64748b' }}>
                Cambiar tipo
              </Button>
            )}
          </Box>

          {previewUrl && (
            <Box sx={{ width: '100%', borderRadius: 2, overflow: 'hidden',
                        border: '1px solid #e2e8f0', maxHeight: 300,
                        display: 'flex', justifyContent: 'center',
                        bgcolor: '#f8fafc', mb: 1 }}>
              <img src={previewUrl} alt="Evidencia"
                style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }} />
            </Box>
          )}

          {subiendo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, justifyContent: 'center' }}>
              <CircularProgress size={14} sx={{ color: '#0d9488' }} />
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>Subiendo foto…</Typography>
            </Box>
          )}

          {guardado && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ fontSize: 12, mb: 1 }}>
              Foto guardada. Lista para el check-in.
            </Alert>
          )}
        </>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {guardado && (
          <Button fullWidth variant="contained" size="large" disableElevation
            startIcon={<CheckCircleIcon />}
            onClick={() => onConfirm?.()}
            sx={{ textTransform: 'none', fontWeight: 700,
                   background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
            Confirmar check-in
          </Button>
        )}
        {tipoFoto && previewUrl && !subiendo && (
          <Button fullWidth variant="outlined" startIcon={<ReplayIcon />}
            onClick={() => { setPreviewUrl(null); setGuardado(false); inputRef.current?.click(); }}
            sx={{ textTransform: 'none' }}>
            Re-tomar foto
          </Button>
        )}
        <Button fullWidth onClick={onCancel} sx={{ textTransform: 'none', color: '#64748b' }}>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
}
